import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingBag, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import { formatMoney } from '../utils/money';

const ProductCard = ({ product, onAddToCart }) => {
  const { settings } = useSettings();
  const [hovered, setHovered] = useState(false);
  const [added, setAdded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdded(true);
    onAddToCart && onAddToCart(product);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.22 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--white)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        border: '1px solid var(--mist)',
        boxShadow: hovered ? 'var(--shadow-hover)' : 'var(--shadow)',
        transition: 'box-shadow 0.3s ease',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Link to={`/product/${product.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Product image */}
        <div style={{
          height: 'clamp(150px, 22vw, 220px)',
          background: product.gradient,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {!imgError ? (
            <img
              src={product.image}
              alt={product.name}
              onError={() => setImgError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                transition: 'transform 0.4s ease',
                transform: hovered ? 'scale(1.05)' : 'scale(1)',
              }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: product.gradient,
            }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--mid)' }}>{product.name}</span>
            </div>
          )}

          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 50%)',
          }} />

          {product.badge && (
            <div style={{
              position: 'absolute', top: '10px', left: '10px',
              background: product.badgeColor,
              color: 'var(--white)',
              borderRadius: '5px',
              padding: '3px 8px',
              fontSize: 'clamp(0.55rem, 1.8vw, 0.65rem)', fontWeight: 700,
              fontFamily: 'Syne', letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>{product.badge}</div>
          )}
          {product.originalPrice && (
            <div style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'var(--white)',
              color: '#e05c3a',
              borderRadius: '5px',
              padding: '3px 7px',
              fontSize: 'clamp(0.55rem, 1.8vw, 0.65rem)', fontWeight: 700,
            }}>
              -{Math.round((1 - product.price / product.originalPrice) * 100)}%
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding: 'clamp(10px, 2vw, 16px) clamp(12px, 2.5vw, 18px) clamp(12px, 2.5vw, 18px)', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '5px', gap: '4px',
          }}>
            <span style={{
              fontSize: 'clamp(0.58rem, 1.8vw, 0.68rem)', fontWeight: 600,
              color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>{product.category}</span>
            <span style={{
              fontSize: 'clamp(0.58rem, 1.8vw, 0.68rem)', fontWeight: 600,
              color: product.effectColor,
              background: product.effectColor + '18',
              padding: '2px 6px', borderRadius: '4px',
              whiteSpace: 'nowrap',
            }}>{product.effect}</span>
          </div>

          <h3 style={{
            fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(0.8rem, 2.2vw, 0.95rem)',
            color: 'var(--charcoal)', marginBottom: '10px',
            letterSpacing: '-0.02em', lineHeight: '1.3',
          }}>{product.name}</h3>

          {/* Rating + onset */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            marginBottom: '12px', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Star size={10} fill="var(--gold)" color="var(--gold)" />
              <span style={{ fontSize: 'clamp(0.68rem, 2vw, 0.75rem)', fontWeight: 600, color: 'var(--charcoal)' }}>
                {product.rating}
              </span>
              <span style={{ fontSize: 'clamp(0.65rem, 1.9vw, 0.72rem)', color: 'var(--light-text)' }}>
                ({product.reviews})
              </span>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              fontSize: 'clamp(0.65rem, 1.9vw, 0.72rem)', color: 'var(--mid)',
            }}>
              <Zap size={9} />
              <span>{product.onset}</span>
            </div>
          </div>

          {/* Price & CTA */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
              <span style={{
                fontFamily: 'DM Sans', fontWeight: 700, fontSize: 'clamp(1rem, 3vw, 1.15rem)',
                color: 'var(--forest)',
              }}>{formatMoney(product.price, settings)}</span>
              {product.originalPrice && (
                <span style={{
                  fontSize: 'clamp(0.72rem, 2vw, 0.82rem)', color: 'var(--light-text)',
                  textDecoration: 'line-through',
                  fontFamily: 'DM Sans',
                }}>{formatMoney(product.originalPrice, settings)}</span>
              )}
            </div>
            <button
              onClick={handleAdd}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2vw, 14px)',
                borderRadius: '6px',
                background: added ? 'var(--accent-green)' : 'var(--forest)',
                color: 'var(--white)',
                border: 'none', cursor: 'pointer',
                fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(0.65rem, 1.8vw, 0.72rem)',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                transition: 'all 0.22s',
                whiteSpace: 'nowrap',
              }}
            >
              <ShoppingBag size={11} />
              {added ? 'Added' : 'Add'}
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
